/**
 * ⏱️ **What the player's machine is actually doing** — T186.
 *
 * ## 🔴 Why this file exists: the old readout could not see a stutter
 *
 * 🎯 **haugur, 2026-09-15:** *"just standing still in the world and spinning
 * around i get a little tearing and studdering… Still my hud s telling me that
 * im at 60fps."*
 *
 * **He was right and the HUD was wrong.** The frame rate was one line in
 * `main.ts`:
 *
 * ```
 * fps = fps * 0.9 + (1 / dt) * 0.1
 * ```
 *
 * 🔴 **That is a smoothed average, and a stutter is by definition the rare bad
 * frame.** One frame that freezes for 200 ms pulls the number from 60 down to
 * **54.5** and it springs back within about twenty frames. So the readout told
 * the truth about the *good* frames and hid the only frame anybody feels.
 *
 * ⚠️ **This is *measuring the thing next to the thing*** — the fault recorded
 * more times in this repo than any other — **wearing an fps counter.** An
 * average frame rate is a real number about a real thing; it is simply not the
 * thing the complaint was about.
 *
 * ## 👉 So: report the worst, not the mean
 *
 * | | what it says |
 * |---|---|
 * | `fps` | the steady state — kept, because it is still the right number for *is this machine coping at all* |
 * | `worstMs` | 🔴 **the longest single frame in the window.** This is the stutter |
 * | `hitches` | how many frames in the window took longer than {@link HITCH_MS}. One is a blip; twenty a second is what *"studdering"* means |
 *
 * ## ⚠️ A window, not all time
 *
 * The worst frame *ever* is always the first one — a cold shader cache, the
 * first chunk, the texture upload. A number that never improves is a number
 * nobody reads twice, so the window slides and the readout describes **now**.
 *
 * 🔴 **Kept free of `three`, the DOM and `performance.now()`** — every function
 * takes the time it should use. The same argument `streaming.ts` makes: the
 * arithmetic is where the bugs are, and arithmetic that needs a browser to run
 * is arithmetic nobody tests.
 */

/** How long a frame must take to be counted a hitch, in milliseconds. */
export const HITCH_MS = 50;

/**
 * How long a window is, in milliseconds.
 *
 * ⚠️ **One second, because that is how long a person waits before looking
 * again.** Shorter and the number never settles enough to read; longer and a
 * stutter you have already walked away from is still on the screen.
 */
export const WINDOW_MS = 1000;

/**
 * 🔴 **HOW FAR BACK A BUG REPORT CAN SEE, in milliseconds.**
 *
 * 🎯 **haugur, 2026-09-17:** *"when i fly to the shore my framerate drops to 2
 * to 5 fps as the worl blocks are beeing drawn."*
 *
 * ⚠️ **His report said `60 fps`, and it was not lying.** {@link WINDOW_MS} is
 * one second, and he pressed F8 **after landing**, once the world had settled.
 * In that second there genuinely were no slow frames.
 *
 * > ## 🔴 **NOBODY PRESSES F8 DURING THE STALL. THEY PRESS IT AFTER THEY
 * > NOTICE.**
 *
 * **So the instrument described the aftermath and not the fault**, and it took
 * him going back and re-testing by hand to find what the report should have
 * said on its own.
 *
 * ⚠️ **The one-second window is NOT widened, and that is deliberate.** Its own
 * comment argues the case and the case is right — **for the HUD**: *"longer and
 * a stutter you have already walked away from is still on the screen."* **The
 * HUD wants NOW. A bug report wants WHAT JUST HAPPENED.** Two questions, two
 * numbers.
 *
 * ✅ **Thirty seconds** — long enough to cover flying somewhere and landing,
 * short enough that it is still this bug rather than the last one.
 */
export const MEMORY_MS = 30_000;

/** ⚠️ Cap the stored samples so a long pause cannot grow this without bound. */
const MAX_SAMPLES = 600;

/**
 * One second of frames, boiled down.
 *
 * ⚠️ **Buckets rather than every frame.** Thirty seconds of samples would be
 * ~1,800 numbers kept for one row of one bug report; **thirty buckets answer
 * the same question exactly**, because the worst of the worsts is the worst.
 */
interface Second {
  /** Which second this is, on the same monotonic clock `record` is given. */
  readonly at: number;
  worstMs: number;
  hitches: number;
}

export interface FrameReport {
  /** Smoothed frames per second — the steady state. */
  readonly fps: number;
  /** 🔴 The longest single frame in the window, in milliseconds. The stutter. */
  readonly worstMs: number;
  /** How many frames in the window took longer than {@link HITCH_MS}. */
  readonly hitches: number;
  /** How many frames the window holds — so a reader can tell 0 from "no data". */
  readonly samples: number;
  /**
   * 🔴 **The worst single frame in the last {@link MEMORY_MS}** — the number a
   * bug report is actually for.
   *
   * ⚠️ **Never smaller than {@link FrameReport.worstMs}**, because the second is
   * inside the thirty.
   */
  readonly rememberedWorstMs: number;
  /** How many slow frames in the last {@link MEMORY_MS}. */
  readonly rememberedHitches: number;
  /** How many seconds the memory actually holds, so a reader can tell 0 from "just started". */
  readonly rememberedSeconds: number;
}

/**
 * Frame times over a sliding window.
 *
 * ⚠️ **`record` takes the frame's duration AND the time it ended**, rather than
 * calling a clock itself. A test can then drive a whole second of frames
 * without waiting a second.
 */
export class FrameTimes {
  private readonly times: number[] = [];
  private readonly durations: number[] = [];
  private smoothed = 0;
  /** 🔴 One bucket per second, for {@link MEMORY_MS}. See {@link Second}. */
  private readonly memory: Second[] = [];

  /**
   * Note one frame.
   *
   * @param ms  how long the frame took, in milliseconds
   * @param now when it finished, on any monotonic clock
   */
  record(ms: number, now: number): void {
    /* ⚠️ Guard the divide, and guard it at ONE MILLISECOND rather than at
       nearly zero.

       A `dt` of zero happens on the first frame and on any browser that
       coalesces two callbacks. Dividing by it gives Infinity, which poisons the
       smoothed value permanently — Infinity times 0.9 is still Infinity.

       🔴 **But a floor of 1e-3 only moves the problem.** It turns one bad frame
       into a sample of a MILLION frames a second, and at a decay of 0.9 that
       takes about 150 frames — two and a half seconds — to wash out. For those
       two and a half seconds the readout is nonsense, which is exactly the
       failure this whole file was written to stop.

       ✅ **No browser draws a genuine sub-millisecond frame.** Treating one as
       1 ms caps a bad sample at 1,000 fps, which decays away in about thirty. */
    const safe = Math.max(ms, 1);
    this.smoothed = this.smoothed * 0.9 + (1000 / safe) * 0.1;

    this.times.push(now);
    this.durations.push(ms);

    /* 🔴 The longer memory. ⚠️ Bucketed by whole seconds of the clock it is
       given, so a test can drive thirty seconds without waiting thirty. */
    const second = Math.floor(now / 1000);
    const last = this.memory.at(-1);
    if (last === undefined || last.at !== second) {
      this.memory.push({ at: second, worstMs: ms, hitches: ms >= HITCH_MS ? 1 : 0 });
    } else {
      if (ms > last.worstMs) last.worstMs = ms;
      if (ms >= HITCH_MS) last.hitches++;
    }
    /* ⚠️ Whole seconds, so the cut is `>=` on the count rather than on the
       clock — a browser that pauses for a minute must not leave a stale bucket
       claiming the last thirty seconds were terrible. */
    while (this.memory.length > 0 && (second - this.memory[0]!.at) * 1000 >= MEMORY_MS) {
      this.memory.shift();
    }

    /* Drop anything that has fallen out of the window, and then anything over
       the cap — oldest first, so the window is always the most recent frames. */
    while (this.times.length > 0 && now - this.times[0]! > WINDOW_MS) {
      this.times.shift();
      this.durations.shift();
    }
    while (this.times.length > MAX_SAMPLES) {
      this.times.shift();
      this.durations.shift();
    }
  }

  /** What the window says right now, and what the last half-minute remembers. */
  report(): FrameReport {
    let worstMs = 0;
    let hitches = 0;
    for (const ms of this.durations) {
      if (ms > worstMs) worstMs = ms;
      if (ms >= HITCH_MS) hitches++;
    }
    let rememberedWorstMs = 0;
    let rememberedHitches = 0;
    for (const s of this.memory) {
      if (s.worstMs > rememberedWorstMs) rememberedWorstMs = s.worstMs;
      rememberedHitches += s.hitches;
    }
    return {
      fps: this.smoothed,
      worstMs,
      hitches,
      samples: this.durations.length,
      rememberedWorstMs,
      rememberedHitches,
      rememberedSeconds: this.memory.length,
    };
  }
}

export interface ChunkWaitReport {
  /** The middle wait, in milliseconds — what a typical chunk costs. */
  readonly medianMs: number;
  /** The longest wait in the window, in milliseconds. */
  readonly worstMs: number;
  /** How many arrivals the window holds. */
  readonly samples: number;
  /** How many chunks have been asked for and not yet come back. */
  readonly outstanding: number;
}

/**
 * ⏱️ **How long the world takes to arrive** — request to delivery, per chunk.
 *
 * 🔴 **This is the number T185 and T214 will be judged on, and it is NOT a
 * ping.** A ping would measure the wire and nothing else. What a player feels
 * when they fly is *ask for ground → stand on ground*, which includes the
 * server generating it — **and on 2026-09-15 that turned out to be where nearly
 * all of the time went**: 42 ms a chunk, sequentially, while both containers
 * sat near 0% CPU.
 *
 * ⚠️ **A ping would have reported that everything was fine.** It was measured
 * with `tools/lagprobe.mjs`, which asks the same question this class does.
 *
 * ✅ **And it needs no protocol change**, which is why it is this and not a
 * `ping` message: the client already knows when it asked.
 */
export class ChunkWaits {
  private readonly asked = new Map<string, number>();
  private readonly waits: number[] = [];
  private readonly at: number[] = [];

  /** Note that a chunk was asked for. */
  request(key: string, now: number): void {
    /* ⚠️ Never overwrite an earlier ask. A chunk re-requested after the
       watchdog reclaimed its slot would otherwise report the SECOND ask's short
       wait and hide the stall that caused it — which is the exact shape of
       fault this instrument exists to catch. */
    if (!this.asked.has(key)) this.asked.set(key, now);
  }

  /** Note that a chunk arrived. Unknown keys are ignored, not guessed at. */
  arrive(key: string, now: number): void {
    const sent = this.asked.get(key);
    if (sent === undefined) return;
    this.asked.delete(key);

    this.waits.push(now - sent);
    this.at.push(now);

    while (this.at.length > 0 && now - this.at[0]! > WINDOW_MS * 10) {
      this.at.shift();
      this.waits.shift();
    }
    while (this.at.length > MAX_SAMPLES) {
      this.at.shift();
      this.waits.shift();
    }
  }

  /**
   * Forget everything outstanding.
   *
   * ⚠️ **Called when the ring is rebuilt or the socket drops.** Without it,
   * chunks that will never arrive sit in `asked` for ever and `outstanding`
   * climbs until it is meaningless.
   */
  reset(): void {
    this.asked.clear();
  }

  report(): ChunkWaitReport {
    const sorted = [...this.waits].sort((a, b) => a - b);
    return {
      medianMs: median(sorted),
      worstMs: sorted.length === 0 ? 0 : sorted[sorted.length - 1]!,
      samples: sorted.length,
      outstanding: this.asked.size,
    };
  }
}

/**
 * The middle value of an already-sorted list; 0 for an empty one.
 *
 * ⚠️ **`.at()` rather than `[mid]`** — a computed index into an array is what
 * the linter's object-injection rule objects to, and it is right to object in
 * general even though `mid` is derived from the array's own length here.
 */
function median(sorted: readonly number[]): number {
  if (sorted.length === 0) return 0;
  const mid = Math.floor(sorted.length / 2);
  const upper = sorted.at(mid) ?? 0;
  /* Even counts average the two middles, so the median of [10, 20] is 15 rather
     than whichever of them the index happens to land on. */
  return sorted.length % 2 === 0 ? ((sorted.at(mid - 1) ?? 0) + upper) / 2 : upper;
}
